import { useState, useEffect } from 'react';

interface ResponsiveState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
  isLandscapeMobile: boolean;  // For landscape mobile detection
  isLandscapeTablet: boolean;  // For landscape tablet detection
  isLandscapeDevice: boolean;  // Combined property for any landscape mobile/tablet
  width: number;
  height: number;
  devicePixelRatio: number;
}

export const useResponsive = (): ResponsiveState => {
  // Initialize with sensible defaults for SSR
  const [state, setState] = useState<ResponsiveState>({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isPortrait: false,
    isLandscape: true,
    isLandscapeMobile: false,
    isLandscapeTablet: false,
    isLandscapeDevice: false,
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
  });

  useEffect(() => {
    // Handle resize for responsive layout
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isPortrait = height > width;
      const isLandscape = !isPortrait;
      const isMobile = width < 768;
      const isTablet = width >= 768 && width < 1024;
      const isLandscapeMobile = isMobile && isLandscape;
      const isLandscapeTablet = isTablet && isLandscape;
      
      setState({
        isMobile,
        isTablet,
        isDesktop: width >= 1024,
        isPortrait,
        isLandscape,
        isLandscapeMobile,
        isLandscapeTablet,
        isLandscapeDevice: isLandscape && (isMobile || isTablet), // Combined flag
        width,
        height,
        devicePixelRatio: window.devicePixelRatio || 1,
      });
    };
    
    // Set initial state
    handleResize();
    
    // Add event listener with debounce for performance
    let resizeTimer: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 100);
    };
    
    window.addEventListener('resize', debouncedResize);
    
    // Handle orientation change explicitly for mobile/tablet devices
    window.addEventListener('orientationchange', () => {
      // Run immediately for better UX and then also with debounce
      handleResize();
      // Also run after a small delay to ensure browser has fully updated dimensions
      setTimeout(handleResize, 300);
    });
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);
  
  return state;
};

export default useResponsive;
